import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { Sparkles, ArrowRight } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import PropertyCard from "@/components/PropertyCard";
import { toast } from "sonner";

const CATEGORIES = [
  { value: "PLOT", label: "Residential / Commercial Plot" },
  { value: "LAND", label: "Agricultural / Industrial Land" },
  { value: "HOUSE", label: "House" },
  { value: "FLAT", label: "Flat / Apartment" },
  { value: "VILLA", label: "Villa" },
  { value: "COMMERCIAL", label: "Commercial Property" },
];

export default function Recommendations() {
  const { register, handleSubmit, setValue, watch, formState: { isSubmitting } } = useForm({
    defaultValues: { city: "Bengaluru", budget_max: 15000000, category: "" }
  });
  const [result, setResult] = useState(null);
  const cat = watch("category");

  const onSubmit = async (values) => {
    try {
      const payload = { ...values, budget_max: Number(values.budget_max), area_min: values.area_min ? Number(values.area_min) : undefined, bedrooms: values.bedrooms ? Number(values.bedrooms) : undefined };
      if (!payload.category) delete payload.category;
      const { data } = await api.post("/recommendations", payload);
      setResult(data);
      toast.success("Personalized matches ready");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="bg-background min-h-screen" data-testid="recommendations-page">
      <section className="py-16 lg:py-20 bg-secondary">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-2xl">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-muted-foreground mb-3 inline-flex items-center gap-1.5"><Sparkles className="w-3 h-3"/>Free Recommendation Engine</p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-primary leading-[1.05]">Get matched to homes in <em className="font-light brand-gradient-text">seconds</em></h1>
            <p className="text-muted-foreground mt-4 text-base lg:text-lg">Choose your preferences to review published properties that match the available listing data.</p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="mt-10 bg-background border border-border rounded-lg p-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="recommend-form">
            <div className="space-y-1.5"><Label>City</Label><Input {...register("city", { required: true })} data-testid="rec-city"/></div>
            <div className="space-y-1.5">
              <Label>Category</Label>
              <Select value={cat || "any"} onValueChange={(v) => setValue("category", v === "any" ? "" : v)}>
                <SelectTrigger data-testid="rec-category"><SelectValue placeholder="Any"/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any</SelectItem>
                  {CATEGORIES.map(({ value, label }) => <SelectItem key={value} value={value}>{label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Max Budget (₹)</Label><Input type="number" min="0" {...register("budget_max", { required: true, min: 0 })} data-testid="rec-budget"/></div>
            <div className="space-y-1.5"><Label>Min Bedrooms</Label><Input type="number" min="0" {...register("bedrooms")} data-testid="rec-beds"/></div>
            <div className="sm:col-span-2 lg:col-span-4">
              <Button type="submit" size="lg" disabled={isSubmitting} className="w-full sm:w-auto font-bold gap-2" data-testid="rec-submit">
                {isSubmitting ? "Finding…" : "Find my matches"} <ArrowRight className="w-4 h-4"/>
              </Button>
            </div>
          </form>
        </div>
      </section>

      {result && (
        <section className="py-16 lg:py-20 space-y-16">
          <Bucket title="Best matches for you" desc="Properties within your budget and criteria." items={result.best_match}/>
          <Bucket title="New listings nearby" desc="Just listed in your city." items={result.new_listings}/>
          <Bucket title="Top-viewed properties" desc="What other buyers are loving." items={result.top_rated}/>
        </section>
      )}
    </div>
  );
}

function Bucket({ title, desc, items }) {
  if (!items?.length) return null;
  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-12">
      <div className="mb-6">
        <h2 className="font-display text-2xl sm:text-3xl font-bold text-primary">{title}</h2>
        <p className="text-muted-foreground mt-1">{desc}</p>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.slice(0,6).map(p => <PropertyCard key={p.property_id} property={p}/>)}
      </div>
    </div>
  );
}
